import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowUpIcon from '@wix/da-shared-react/pkg/Icons/ArrowUp';
import ArrowDownIcon from '@wix/da-shared-react/pkg/Icons/ArrowDown';
import TrashcanIcon from '@wix/da-shared-react/pkg/Icons/24x24/Trashcan';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import { BiEventType } from '@wix/da-shared-react/pkg/biLogger/constants';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import EditConfigButton from './EditConfigButton';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/TextTooltipOnHover';

import s from './WidgetEditControls.scss';

export interface Props {
  className?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
  onEdit?: () => void;
  tooltipTitle?: string;
  tooltipContent?: React.ReactNode;
  withFTUE?: boolean;
}

const WidgetEditControls: React.FC<Props> = ({
  tooltipTitle,
  tooltipContent,
  onEdit,
  onMoveUp,
  onRemove,
  withFTUE,
  className,
  onMoveDown,
}) => {
  const { t } = useTranslation();
  if (!onEdit && !onMoveDown && !onMoveUp && !onRemove) {
    return null;
  }

  const renderEditConfigButton = () => {
    if (tooltipContent) {
      return (
        <TextTooltipOnHover
          placement="top"
          title={tooltipTitle}
          tooltipText={tooltipContent}
          renderReference={({ ref }) => (
            <EditConfigButton
              onClick={onEdit}
              withFTUE={withFTUE}
              iconRef={ref}
            />
          )}
        />
      );
    }

    return <EditConfigButton onClick={onEdit} withFTUE={withFTUE} />;
  };

  return (
    <div className={classnames(s['root'], className)}>
      {onMoveUp && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.moveUp')}
          onClick={onMoveUp}
          className={s['icon-button']}
          data-role="edit-control"
          biData={{ evid: BiEventType.MOVE_WIDGET_CLICK, direction: 'up' }}
          aria-label={t('widgets.common.moveUp')}
        >
          <ArrowUpIcon size={IconSize.MEDIUM} className={s['icon']} condensed />
        </IconButton>
      )}
      {onMoveDown && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.moveDown')}
          onClick={onMoveDown}
          className={s['icon-button']}
          data-role="edit-control"
          biData={{ evid: BiEventType.MOVE_WIDGET_CLICK, direction: 'down' }}
          aria-label={t('widgets.common.moveDown')}
        >
          <ArrowDownIcon
            size={IconSize.MEDIUM}
            className={s['icon']}
            condensed
          />
        </IconButton>
      )}
      {onRemove && (
        <IconButton
          noHoverEffect
          tooltipText={t!('widgets.common.delete')}
          onClick={onRemove}
          className={s['icon-button']}
          data-role="edit-control"
          biData={{ evid: BiEventType.REMOVE_WIDGET_CLICK }}
          aria-label={t('widgets.common.delete')}
        >
          <TrashcanIcon
            size={IconSize.MEDIUM}
            className={s['icon']}
            condensed
          />
        </IconButton>
      )}
      {onEdit && renderEditConfigButton()}
    </div>
  );
};

WidgetEditControls.displayName = 'WidgetEditControls';

export default WidgetEditControls;
